import { APP_INITIALIZER, ApplicationConfig, provideZoneChangeDetection, isDevMode } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { environment } from '../environments/environment';
import { ConfigService } from './config.service';
import { routes } from './app.routes';
import { provideAppCheck, initializeAppCheck, ReCaptchaEnterpriseProvider } from '@angular/fire/app-check';
import { MatDialogRef } from '@angular/material/dialog';
import { provideServiceWorker } from '@angular/service-worker';
import { AnonymousAuthInterceptor } from './interceptors/anonymous-auth.interceptor';

// Function to load config based on the environment
export function configInitializer(configService: ConfigService) {
  return (): Promise<any> => {

    const hostname = window.location.hostname;
    const requestedTenant = new URLSearchParams(window.location.search).get('tenant');
    if (requestedTenant) {
      // Shared Firebase Hosting is the dev front door for clinics that do not
      // own a custom-domain certificate. Keep the override deliberately
      // narrow: it becomes one path segment in the public config request.
      if (!/^[a-z0-9-]+$/.test(requestedTenant)) {
        throw new Error('Invalid tenant in the URL');
      }
      return configService.loadConfig(requestedTenant).toPromise();
    }
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
       (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      return configService.loadConfig(environment.tenantId).toPromise();
    }
    const subdomain = hostname.split('.')[0];
    if (!subdomain) {
      throw new Error('Subdomain not found in the URL');
    }
    const tenantId = subdomain;
    if (!tenantId) {
      throw new Error('Client code not found in the URL');
    }
    return configService.loadConfig(tenantId).toPromise();
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    {
      provide: APP_INITIALIZER,
      useFactory: configInitializer,
      deps: [ConfigService],
      multi: true
    },
    {
        provide: MatDialogRef,
        useValue: {}
    },
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AnonymousAuthInterceptor,
      multi: true
    },
    // Initialize Firebase App with environment configuration
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    // Initialize Firebase Authentication
    provideAuth(() => getAuth()),
    // Initialize Firebase App Check with ReCaptcha provider
    // DISABLED FOR LOCAL TESTING
    // provideAppCheck(() => initializeAppCheck(getApp(), {
    //   provider: new ReCaptchaEnterpriseProvider(environment.recaptcha.siteKey),
    //   isTokenAutoRefreshEnabled: true
    // }),
    provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000'
          })
  ]
};
